import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery
} from "@tanstack/react-query";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// GET /location/{id}/

export const LocationResponseSchema = z.object({
  id: z.number(),
  board: z.number(),
  street_address: z.string().nullish(),
  city: z.string().nullish(),
  state_or_region: z.string().nullish(),
  postal_code: z.string().nullish(),
  country: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  property_name: z.string().nullish(),
  building_name: z.string().nullish(),
  floor_or_level: z.string().nullish(),
  elevation: z.string().nullish(),
  work_zone_name: z.string().nullish(),
  primary_entry_route: z.string().nullish(),
  staging_area: z.string().nullish(),
  start_here_note: z.string().nullish(),
  readiness_state: z.enum(["thin", "partial", "field_ready"]).nullish(),
  location_summary: z.string().nullish(),
  workforce_handoff_note: z.string().nullish(),
  archived: z.boolean().nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  creator: z.number().nullable().optional(),
  updater: z.number().nullable().optional()
});

export type LocationResponse = z.infer<typeof LocationResponseSchema>;

const RequestSchema = z.object({
  id: z.number()
});

type RequestType = z.infer<typeof RequestSchema>;

const QUERY_KEY = ["location"] as const;

const fetcher = (params: RequestType) =>
  api<RequestType, LocationResponse>({
    method: "GET",
    url: `/location/:id/`,
    requestSchema: RequestSchema,
    responseSchema: LocationResponseSchema,
    body: params
  });

type UseLocationOptions = {
  variables: RequestType;
  enabled?: boolean;
};

export function useLocation({ variables, enabled = true }: UseLocationOptions) {
  return useQuery({
    queryKey: [...QUERY_KEY, variables] as const,
    queryFn: () => fetcher(variables),
    placeholderData: keepPreviousData,
    enabled
  });
}

export function useLocationSuspense(variables: RequestType) {
  return useSuspenseQuery<LocationResponse>({
    queryKey: [...QUERY_KEY, variables],
    queryFn: () => fetcher(variables)
  });
}

export const locationQuery = {
  queryKey: QUERY_KEY,
  fetcher
};
